/**
 * Semantic color maps — CEFR levels and skill categories.
 *
 * The hues live as CSS variables in index.css (light + dark); here we
 * map domain values to the Tailwind utility classes that read them.
 * Class strings are written out in full (not built by interpolation) so
 * Tailwind's source scanner actually emits them.
 *
 * Anything unknown falls back to the neutral ink scale, so a new level
 * or category code degrades gracefully instead of vanishing.
 */

// ── CEFR levels ──────────────────────────────────────────────────────

const CEFR_TEXT: Record<string, string> = {
  A1: "text-cefr-a1",
  A2: "text-cefr-a2",
  B1: "text-cefr-b1",
  B2: "text-cefr-b2",
  C1: "text-cefr-c1",
  C2: "text-cefr-c2",
};

const CEFR_BG: Record<string, string> = {
  A1: "bg-cefr-a1",
  A2: "bg-cefr-a2",
  B1: "bg-cefr-b1",
  B2: "bg-cefr-b2",
  C1: "bg-cefr-c1",
  C2: "bg-cefr-c2",
};

const CEFR_BORDER: Record<string, string> = {
  A1: "border-cefr-a1",
  A2: "border-cefr-a2",
  B1: "border-cefr-b1",
  B2: "border-cefr-b2",
  C1: "border-cefr-c1",
  C2: "border-cefr-c2",
};

function normLevel(level: string | null | undefined): string {
  return (level ?? "").trim().toUpperCase();
}

/** Text color class for a CEFR level letter (e.g. "text-cefr-b1"). */
export function cefrText(level: string | null | undefined): string {
  return CEFR_TEXT[normLevel(level)] ?? "text-ink-700";
}

/** Solid background class for a CEFR level (bars, dots). */
export function cefrBg(level: string | null | undefined): string {
  return CEFR_BG[normLevel(level)] ?? "bg-ink-400";
}

/** Border color class for a CEFR level. */
export function cefrBorder(level: string | null | undefined): string {
  return CEFR_BORDER[normLevel(level)] ?? "border-ink-200";
}

// ── Skill categories ─────────────────────────────────────────────────

const CAT_TEXT: Record<string, string> = {
  gram: "text-cat-gram",
  vocab: "text-cat-vocab",
  phono: "text-cat-phono",
  morpho: "text-cat-morpho",
  pragma: "text-cat-pragma",
  lex: "text-cat-lex",
};

const CAT_BG: Record<string, string> = {
  gram: "bg-cat-gram",
  vocab: "bg-cat-vocab",
  phono: "bg-cat-phono",
  morpho: "bg-cat-morpho",
  pragma: "bg-cat-pragma",
  lex: "bg-cat-lex",
};

/** Text color class for a skill-category code (e.g. "text-cat-vocab"). */
export function categoryText(code: string | null | undefined): string {
  return CAT_TEXT[(code ?? "").trim().toLowerCase()] ?? "text-ink-400";
}

/** Solid background class for a skill category (dots, tags). */
export function categoryBg(code: string | null | undefined): string {
  return CAT_BG[(code ?? "").trim().toLowerCase()] ?? "bg-ink-300";
}

// ── Mastery bars ─────────────────────────────────────────────────────

/** Fill class for a mastery/retrievability bar, `p` in [0,1]. Single hue
 *  (the accent) at three opacities so a strong skill reads solid and a
 *  weak one faded — in-palette, replacing the old green/amber/red traffic
 *  light that clashed with any non-warm accent. The bar's WIDTH already
 *  carries the value; opacity is just reinforcement. */
export function masteryBar(p: number): string {
  const pct = p * 100;
  if (pct >= 80) return "bg-accent";
  if (pct >= 40) return "bg-accent/70";
  return "bg-accent/40";
}
